import React, { useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Form,
  Select,
  Input,
  Button,
  Icon,
  DatePicker,
  Radio,
  Divider,
  Tag,
  notification,
} from "antd";
import NumberFormat from "react-number-format";
import moment from "moment";
import { useSelector } from "react-redux";
import useStockMovementContext from "../../../../hooks/useStockMovementContext";
import SelectStockItem from "../../../../components/utils/selectStockItem";
import SelectUnit from "../../../../components/utils/selectUnit";
import {
  applyConversionFactor,
  computeExpirationByDays,
  emptyLocation,
  validateMaterialLine,
} from "../../../../utils/stockMovement";
import {
  MaterialLineCard,
  FiscalBlock,
  LocationRow,
  InputOption,
} from "./styles";
import { numberMask } from "../../../../services/helpersMethodsService";
import ButtonStandard from "../../../../components/utils/button";
import { useState } from "react";
import {
  callSaveStockItemBatch,
  callSaveStockItemVariant,
} from "../../../../services/stockItemService";
import useUnitContext from "../../../../hooks/useUnitContext";

const { Option } = Select;

const CreateNewOption = ({
  value,
  onInputChange,
  onSave,
  onCancel,
  label,
  translation,
}) => (
  <Row type="flex" justify="start" gutter={8}>
    <Col span={24}>
      <InputOption placeholder={label} value={value} onChange={onInputChange} />
    </Col>
    <Col style={{ marginTop: 8 }}>
      <ButtonStandard buttonType={"type6"} onClick={onSave}>
        {translation.buttons.save}
      </ButtonStandard>
    </Col>
    <Col style={{ marginTop: 8 }}>
      <ButtonStandard buttonType={"type7"} onClick={onCancel}>
        {translation.buttons.cancel}
      </ButtonStandard>
    </Col>
  </Row>
);

const StockMovementMaterialLine = ({
  line,
  index,
  movementType,
  receiptDate,
  showErrors,
  onChange,
  onRemove,
  canRemove,
}) => {
  const [showNewVariantInput, setShowNewVariantInput] = useState(false);
  const [inputVariantValue, setInputVariantValue] = useState("");

  const [showNewBatchInput, setShowNewBatchInput] = useState(false);
  const [inputBatchValue, setInputBatchValue] = useState("");

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const tf = translation?.stockItem?.movements?.form || {};
  const tloc = tf.location || {};
  const tconv = tf.conversion || {};
  const terr = translation?.stockItem?.movements?.error || {};
  const defaultPlaceholder =
    translation?.defaultPlaceholder || "Informe um valor";

  const lineErrors = validateMaterialLine(line, movementType);
  const hasError = (field) => showErrors && lineErrors[field];

  const {
    stockItems,
    variants,
    batches,
    loadVariants,
    loadStockItems,
    loadBatches,
    addVariant,
    addBatch,
  } = useStockMovementContext();

  const { dataList: unities } = useUnitContext();

  const selectedItem = stockItems.find((i) => i.id === line.stockItemId);
  const hasVariant = selectedItem?.hasVariant;
  const batchControl = selectedItem?.batchControl;
  const itemVariants = useMemo(() => {
    return selectedItem && line.stockItemId ? variants : [];
  }, [selectedItem, line.stockItemId, variants]);
  const itemBatches = useMemo(() => {
    return selectedItem && line.stockItemId ? batches : [];
  }, [selectedItem, line.stockItemId, batches]);

  const update = useCallback(
    (patch) => onChange(index, { ...line, ...patch }),
    [line, index, onChange],
  );

  const handleStockItemCreated = useCallback(
    (newItem) => {
      loadStockItems();
      update({
        stockItemId: newItem.id,
        stockItemMeasurementUnitId: newItem.measurementUnitId || "",
        stockItemMeasurementUnit: newItem.measurementUnitName || "",
        variantId: null,
        batchId: null,
        measurementUnitId: newItem.measurementUnitId || "",
        measurementUnit: newItem.measurementUnitName || "",
      });
    },
    [loadStockItems, update],
  );

  const handleUnitItemChange = useCallback(
    (val) => {
      const selectedUnit = unities.find((u) => u.id === val);
      update({
        measurementUnitId: val,
        measurementUnit: selectedUnit?.name || "",
      });
    },
    [unities, update],
  );

  const handleItemChange = (val) => {
    loadVariants(val);
    loadBatches(val);
    const stockItem = stockItems.find((i) => i.id === val);
    onChange(index, {
      ...line,
      stockItemId: val,
      stockItemMeasurementUnitId: stockItem?.measurementUnitId || "",
      stockItemMeasurementUnit: stockItem?.measurementUnitName || "",
      variantId: null,
      batchId: null,
      measurementUnitId: stockItem?.measurementUnitId || "",
      measurementUnit: stockItem?.measurementUnitName || "",
    });
  };

  const handleFactorChange = (val) => {
    onChange(index, applyConversionFactor(line, val));
  };

  const handleCreateNewVariant = useCallback(
    async (stockItemId) => {
      if (inputVariantValue) {
        const body = {
          internalId: inputVariantValue,
        };

        try {
          const {
            data: { results: newVariant },
          } = await callSaveStockItemVariant({
            groupId,
            farmId,
            itemId: stockItemId,
            variant: body,
          });

          setShowNewVariantInput(false);
          setInputVariantValue("");

          addVariant(stockItemId, newVariant);

          update({ variantId: newVariant.id });

          notification.success({
            message:
              translation?.stockItem?.movements?.notifications
                ?.variantCreated || "Variante criada com sucesso",
          });
        } catch (error) {
          notification.error({
            message:
              translation?.stockItem?.movements?.notifications
                ?.errorVariantCreated || "Erro ao criar variante",
          });
        }
      }
    },
    [inputVariantValue, groupId, farmId, addVariant, update, translation],
  );

  const handleCreateNewBatch = useCallback(
    async (stockItemId) => {
      if (inputBatchValue) {
        const body = {
          batchNumber: inputBatchValue,
        };

        try {
          // Assuming there's a similar API call for saving batches
          const {
            data: { results: newBatch },
          } = await callSaveStockItemBatch({
            groupId,
            farmId,
            itemId: stockItemId,
            batch: body,
          });

          setShowNewBatchInput(false);
          setInputBatchValue("");

          addBatch(stockItemId, newBatch);

          update({ batchId: newBatch.id });

          notification.success({
            message:
              translation?.stockItem?.movements?.notifications?.batchCreated ||
              "Lote criado com sucesso",
          });
        } catch (error) {
          notification.error({
            message:
              translation?.stockItem?.movements?.notifications
                ?.errorBatchCreated || "Erro ao criar lote",
          });
        }
      }
    },
    [inputBatchValue, groupId, farmId, addBatch, update, translation],
  );

  // Localizações
  const updateLocation = (locIndex, field, value) => {
    const locations = line.locations.map((loc, i) =>
      i === locIndex ? { ...loc, [field]: value } : loc,
    );
    update({ locations });
  };
  const addLocation = () =>
    update({ locations: [...line.locations, emptyLocation()] });
  const removeLocation = (locIndex) =>
    update({
      locations: line.locations.filter((_, i) => i !== locIndex),
    });

  const showConversionNotaFiscal =
    line.nfQuantity && line.nfUnit && line.stockItemId;

  const showConversionProduct =
    line.stockItemMeasurementUnitId != null &&
    line.measurementUnitId != null &&
    line.stockItemMeasurementUnitId !== line.measurementUnitId;

  return (
    <MaterialLineCard>
      <Row type="flex" justify="space-between" align="middle">
        <Col>
          <strong>
            {tf.materials || "Material"} #{index + 1}
          </strong>
        </Col>
        {canRemove && (
          <Col>
            <Button
              size="small"
              type="danger"
              ghost
              icon="delete"
              onClick={() => onRemove(index)}
            />
          </Col>
        )}
      </Row>
      <Divider style={{ margin: "8px 0" }} />
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item
            required
            label={
              <span className={hasError("stockItemId") ? "label-error" : ""}>
                {tf.stockItem || "Item de Estoque"} *
              </span>
            }
            validateStatus={hasError("stockItemId") ? "error" : ""}
            help={
              hasError("stockItemId")
                ? terr.stockItemRequired || "Selecione o item de estoque"
                : null
            }
          >
            <SelectStockItem
              value={line.stockItemId}
              onChange={handleItemChange}
              onDeselect={() => handleItemChange("")}
              onCreated={handleStockItemCreated}
              placeholder="Selecione"
            />
            {line.nfProduct && (
              <Row style={{ marginTop: 8 }}>
                <Col span={24}>
                  <Tag color="purple">NF: {line.nfProduct}</Tag>
                </Col>
              </Row>
            )}
          </Form.Item>
        </Col>
        {hasVariant && (
          <Col span={8}>
            <Form.Item label={tf.variant || "Variante"}>
              <Row type="flex" gutter={8}>
                <Col span={20}>
                  <Select
                    value={line.variantId || undefined}
                    onChange={(val) => update({ variantId: val })}
                    placeholder={
                      translation.defaultSelectPlaceholder || "Selecione"
                    }
                    allowClear
                  >
                    {itemVariants.map((v) => (
                      <Option key={v.id} value={v.id}>
                        {v.internalId || v.batchNumber || v.id}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={4}>
                  <Button
                    shape="circle"
                    disabled={showNewVariantInput}
                    icon="plus"
                    onClick={() => setShowNewVariantInput(!showNewVariantInput)}
                  />
                </Col>
              </Row>
              {showNewVariantInput && (
                <CreateNewOption
                  value={inputVariantValue || ""}
                  onInputChange={(e) => setInputVariantValue(e.target.value)}
                  onSave={() => handleCreateNewVariant(line.stockItemId)}
                  onCancel={() => {
                    setShowNewVariantInput(false);
                    setInputVariantValue("");
                  }}
                  label={tf.newVariant || "Nova Variante"}
                  translation={translation}
                />
              )}
            </Form.Item>
          </Col>
        )}
        {batchControl && (
          <Col span={8}>
            <Form.Item label={tf.batch || "Lote"}>
              <Row type="flex" gutter={8}>
                <Col span={20}>
                  <Select
                    value={line.batchId || undefined}
                    onChange={(val) => update({ batchId: val })}
                    placeholder={
                      translation.defaultSelectPlaceholder || "Selecione"
                    }
                    allowClear
                  >
                    {itemBatches.map((b) => (
                      <Option key={b.id} value={b.id}>
                        {b.batchNumber || b.id}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={4}>
                  <Button
                    shape="circle"
                    disabled={showNewBatchInput}
                    icon="plus"
                    onClick={() => setShowNewBatchInput(!showNewBatchInput)}
                  />
                </Col>
              </Row>
              {showNewBatchInput && (
                <CreateNewOption
                  value={inputBatchValue || ""}
                  onInputChange={(e) => setInputBatchValue(e.target.value)}
                  onSave={() => handleCreateNewBatch(line.stockItemId)}
                  onCancel={() => {
                    setShowNewBatchInput(false);
                    setInputBatchValue("");
                  }}
                  label={tf.newBatch || "Novo Lote"}
                  translation={translation}
                />
              )}
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row gutter={12}>
        <Col span={8}>
          <Form.Item label={tf.batchNumber || "Numero do Lote"}>
            <Input
              value={line.batchNumber}
              placeholder={defaultPlaceholder}
              onChange={(e) => update({ batchNumber: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={tf.serialNumber || "Numero de Série"}>
            <Input
              value={line.serialNumber}
              placeholder={defaultPlaceholder}
              onChange={(e) => update({ serialNumber: e.target.value })}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={16}>
          <Form.Item label={tf.specification || "Especificação"}>
            <Input
              value={line.specification}
              placeholder={defaultPlaceholder}
              onChange={(e) => update({ specification: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            required
            label={
              <span
                className={hasError("measurementUnitId") ? "label-error" : ""}
              >
                {tf.measurementUnit || "Unidade de Medida"} *
              </span>
            }
            validateStatus={hasError("measurementUnitId") ? "error" : ""}
            help={
              hasError("measurementUnitId")
                ? terr.measurementUnitRequired ||
                  "Selecione a unidade de medida"
                : null
            }
          >
            <SelectUnit
              value={line.measurementUnitId}
              onChange={(val) => handleUnitItemChange(val)}
              onDeselect={() =>
                update({ measurementUnitId: null, measurementUnit: "" })
              }
              placeholder={defaultPlaceholder}
            />
          </Form.Item>
        </Col>
      </Row>

      {showConversionNotaFiscal && (
        <FiscalBlock>
          <strong>
            {tconv.title || "Conversão de Unidade (NF → Estoque)"}
          </strong>
          <Row gutter={12} style={{ marginTop: 8 }}>
            <Col span={6}>
              <Form.Item label={tconv.nfQty || "Qtd NF"}>
                <Input disabled value={line.nfQuantity} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={tconv.nfUnit || "Unid. NF"}>
                <Input disabled value={line.nfUnit} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                required
                label={
                  <span
                    className={
                      hasError("conversionFactor") ? "label-error" : ""
                    }
                  >
                    {tconv.factor || "Fator"} *
                  </span>
                }
                validateStatus={hasError("conversionFactor") ? "error" : ""}
                help={
                  hasError("conversionFactor")
                    ? terr.conversionFactorRequired ||
                      "Informe o fator de conversão"
                    : null
                }
              >
                <NumberFormat
                  customInput={Input}
                  value={
                    line.conversionFactor == null ? "" : line.conversionFactor
                  }
                  placeholder={defaultPlaceholder}
                  decimalScale={4}
                  allowLeadingZeros={true}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleFactorChange(floatValue)
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={"Unid. Estoque"}>
                <Input disabled value={line.measurementUnit} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <p>
              {numberMask(line.nfQuantity, false, null, null, 5, 5)}{" "}
              {line.nfUnit} ×{" "}
              {numberMask(line.conversionFactor, false, null, null, 5, 5)} ={" "}
              <strong>
                {numberMask(line.quantity, false, null, null, 5, 5)}{" "}
                {line?.measurementUnit ?? ""}
              </strong>
              {line.nfUnitCost && (
                <>
                  {" "}
                  · Custo: {numberMask(line.nfUnitCost, true, null, null, 5, 5)}
                  /{line.nfUnit} →{" "}
                  <strong>
                    {numberMask(line.cost, true, null, null, 5, 5)}/
                    {line?.measurementUnit ?? ""}
                  </strong>
                </>
              )}
            </p>
          </Row>
        </FiscalBlock>
      )}

      {showConversionProduct && (
        <FiscalBlock>
          <strong>
            {tconv.title || "Conversão de Unidade (NF → Estoque)"}
          </strong>
          <Row gutter={12} style={{ marginTop: 8 }}>
            <Col span={4}>
              <Form.Item label={tconv.nfQty || "Qtd NF"}>
                <NumberFormat
                  customInput={Input}
                  placeholder={defaultPlaceholder}
                  value={line.nfQuantity}
                  decimalScale={4}
                  allowLeadingZeros={true}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) => {
                    update({ nfQuantity: floatValue });
                  }}
                  onBlur={() => {
                    update(applyConversionFactor(line, line.conversionFactor));
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={tconv.nfUnit || "Unid. NF"}>
                <Input disabled value={line.measurementUnit} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={tconv.nfUnitCost || "Custo NF"}>
                <NumberFormat
                  customInput={Input}
                  placeholder={defaultPlaceholder}
                  value={line.nfUnitCost}
                  decimalScale={5}
                  allowLeadingZeros={true}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) => {
                    update({ nfUnitCost: floatValue });
                  }}
                  onBlur={() => {
                    update(applyConversionFactor(line, line.conversionFactor));
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                required
                label={
                  <span
                    className={
                      hasError("conversionFactor") ? "label-error" : ""
                    }
                  >
                    {tconv.factor || "Fator"} *
                  </span>
                }
                validateStatus={hasError("conversionFactor") ? "error" : ""}
                help={
                  hasError("conversionFactor")
                    ? terr.conversionFactorRequired ||
                      "Informe o fator de conversão"
                    : null
                }
              >
                <NumberFormat
                  customInput={Input}
                  value={
                    line.conversionFactor == null ? "" : line.conversionFactor
                  }
                  placeholder={defaultPlaceholder}
                  decimalScale={4}
                  allowLeadingZeros={true}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleFactorChange(floatValue)
                  }
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={"Unid. Estoque"}>
                <Input disabled value={line.stockItemMeasurementUnit} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <p>
              {numberMask(line.nfQuantity, false, null, null, 5, 5)}{" "}
              {line.measurementUnit} ×{" "}
              {numberMask(line.conversionFactor, false, null, null, 5, 5)} ={" "}
              <strong>
                {numberMask(line.quantity, false, null, null, 5, 5)}{" "}
                {line?.stockItemMeasurementUnit ?? ""}
              </strong>
              {line.nfUnitCost && (
                <>
                  {" "}
                  · Custo: {numberMask(line.nfUnitCost, true, null, null, 5, 5)}
                  /{line.measurementUnit} →{" "}
                  <strong>
                    {numberMask(line.cost, true, null, null, 5, 5)}/
                    {line?.stockItemMeasurementUnit ?? ""}
                  </strong>
                </>
              )}
            </p>
          </Row>
        </FiscalBlock>
      )}

      <Row gutter={12}>
        <Col span={8}>
          <Form.Item
            required
            label={
              <span className={hasError("quantity") ? "label-error" : ""}>
                {tf.quantity || "Quantidade"} *
              </span>
            }
            validateStatus={hasError("quantity") ? "error" : ""}
            help={
              hasError("quantity")
                ? terr.quantityRequired || "Informe a quantidade"
                : null
            }
          >
            <NumberFormat
              customInput={Input}
              value={line.quantity || ""}
              decimalScale={4}
              decimalSeparator=","
              thousandSeparator="."
              fixedDecimalScale={true}
              placeholder={defaultPlaceholder}
              onValueChange={({ floatValue }) =>
                update({ quantity: floatValue })
              }
            />
          </Form.Item>
        </Col>
        {movementType === "ENTRY" && (
          <Col span={8}>
            <Form.Item
              required
              label={
                <span className={hasError("cost") ? "label-error" : ""}>
                  {tf.unitCost || "Custo Unitário"} *
                </span>
              }
              validateStatus={hasError("cost") ? "error" : ""}
              help={
                hasError("cost")
                  ? terr.costRequired || "Informe o custo unitário"
                  : null
              }
            >
              <NumberFormat
                customInput={Input}
                value={line.cost}
                decimalScale={5}
                decimalSeparator=","
                thousandSeparator="."
                fixedDecimalScale={true}
                addonBefore="R$"
                allowLeadingZeros={true}
                placeholder={defaultPlaceholder}
                onValueChange={({ floatValue }) => update({ cost: floatValue })}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={8}>
          <Form.Item
            label={
              <Row type="flex" justify="start" gutter={8}>
                <Col>
                  <label htmlFor="">{tf.expiration || "Expiração"}</label>
                </Col>
                <Col>
                  <Radio.Group
                    size="small"
                    value={line.expirationMode}
                    onChange={(e) => update({ expirationMode: e.target.value })}
                    style={{ marginBottom: 4 }}
                  >
                    <Radio.Button value="DATE">
                      {(tf.expirationMode && tf.expirationMode.date) || "Data"}
                    </Radio.Button>
                    <Radio.Button value="DAYS">
                      {(tf.expirationMode && tf.expirationMode.days) || "Dias"}
                    </Radio.Button>
                  </Radio.Group>
                </Col>
              </Row>
            }
          >
            {line.expirationMode === "DATE" ? (
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                value={line.expiration ? moment(line.expiration) : null}
                onChange={(date) =>
                  update({
                    expiration: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
              />
            ) : (
              <NumberFormat
                customInput={Input}
                value={line.expirationDays}
                decimalScale={0}
                placeholder="N° de dias"
                onValueChange={({ floatValue }) =>
                  update({
                    expirationDays: floatValue,
                    expiration: computeExpirationByDays(
                      receiptDate,
                      floatValue,
                    ),
                  })
                }
              />
            )}
          </Form.Item>
        </Col>
      </Row>

      {/* Localizações */}
      <Divider style={{ margin: "8px 0" }} orientation="left">
        {tf.locations || "Localizações"}
      </Divider>
      {line.locations.map((loc, locIndex) => (
        <LocationRow key={locIndex}>
          <Row gutter={8} type="flex" align="middle">
            <Col span={4}>
              <Input
                size="small"
                placeholder={tloc.warehouse || "Armaz."}
                value={loc.warehouse}
                onChange={(e) =>
                  updateLocation(locIndex, "warehouse", e.target.value)
                }
              />
            </Col>
            <Col span={4}>
              <Input
                size="small"
                placeholder={tloc.shelf || "Estante"}
                value={loc.shelf}
                onChange={(e) =>
                  updateLocation(locIndex, "shelf", e.target.value)
                }
              />
            </Col>
            <Col span={3}>
              <Input
                size="small"
                placeholder={tloc.rack || "Pratel."}
                value={loc.rack}
                onChange={(e) =>
                  updateLocation(locIndex, "rack", e.target.value)
                }
              />
            </Col>
            <Col span={3}>
              <Input
                size="small"
                placeholder={tloc.drawer || "Gaveta"}
                value={loc.drawer}
                onChange={(e) =>
                  updateLocation(locIndex, "drawer", e.target.value)
                }
              />
            </Col>
            <Col span={4}>
              <Input
                size="small"
                placeholder={tloc.deposit || "Depósito"}
                value={loc.deposit}
                onChange={(e) =>
                  updateLocation(locIndex, "deposit", e.target.value)
                }
              />
            </Col>
            <Col span={4}>
              <Input
                size="small"
                placeholder={tloc.tank || "Tanque"}
                value={loc.tank}
                onChange={(e) =>
                  updateLocation(locIndex, "tank", e.target.value)
                }
              />
            </Col>
            <Col span={2}>
              {line.locations.length > 1 && (
                <Icon
                  type="minus-circle"
                  style={{ color: "#f5222d" }}
                  onClick={() => removeLocation(locIndex)}
                />
              )}
            </Col>
          </Row>
        </LocationRow>
      ))}
      <Button size="small" type="dashed" icon="plus" onClick={addLocation}>
        {tf.locations || "Localizações"}
      </Button>
    </MaterialLineCard>
  );
};

export default StockMovementMaterialLine;
